import { FormProvider, useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ADMIN_NOTIFICATIONS_SET,
  GET_ADMIN_NOTIFICATIONS,
} from '../../../utils/apollo/permission';
import {
  IAdminNotificationSettingsQuery,
  IAdminNotificationSettingsQueryVariables,
  IAdminNotificationSettingsSetMutation,
  IAdminNotificationSettingsSetMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import Loader from '../../../components/Loaders/Loader';
import NotificationsTargets from './NotificationsTargets';
import {
  INotificationSettingsForm,
  INotificationsSettingsTable,
} from './types';
import NotificationsSettingsTable from './NotificationsSettings/NotificationsSettingsTable';
import {
  INITIAL_VALUES,
  toForm,
  toPayload,
  zipSkeletonWithForm,
} from './NotificationsUtils';
import { PrimaryButton } from '../../../components/Button';
import SecondaryButton from '../../../components/Button/SecondaryButton';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../utils/toastMessage';
import * as NotificationsUI from './NotificationsUI';

const Notifications = () => {
  const [tablesData, setTablesData] = useState<INotificationsSettingsTable[]>(
    [],
  );

  const formMethods = useForm<INotificationSettingsForm>({
    defaultValues: INITIAL_VALUES,
    reValidateMode: 'onSubmit',
  });
  const { formState } = formMethods;
  const { isSubmitting, isDirty } = formState;
  const handleLoading = ({
    adminNotificationSettings,
  }: IAdminNotificationSettingsQuery) => {
    const formData = toForm(adminNotificationSettings, INITIAL_VALUES);
    setTablesData(zipSkeletonWithForm(formData));
    formMethods.reset(formData);
  };

  const { loading } = useQuery<
    IAdminNotificationSettingsQuery,
    IAdminNotificationSettingsQueryVariables
  >(GET_ADMIN_NOTIFICATIONS, {
    onCompleted: handleLoading,
  });

  const [update, { loading: updating }] = useMutation<
    IAdminNotificationSettingsSetMutation,
    IAdminNotificationSettingsSetMutationVariables
  >(ADMIN_NOTIFICATIONS_SET, {
    onCompleted: ({ adminNotificationSettingsSet: data }) => {
      if (printGraphqlErrors(data.errors)) {
        return;
      }
      formMethods.reset(toForm(data.entity, INITIAL_VALUES), {
        keepValues: true,
      });

      toastMessage({
        message: 'Changes to your Notification preferences have been saved.',
        type: 'info',
      });
    },
  });

  const onSubmitForm = formMethods.handleSubmit(async (values) => {
    await update({
      variables: {
        input: toPayload(values),
      },
    });
  });

  if (loading) {
    return <Loader loadingText="Loading..." />;
  }

  return (
    <NotificationsUI.Container>
      <NotificationsUI.Title>Admin Notification Settings</NotificationsUI.Title>
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmitForm}>
          <NotificationsTargets />
          <NotificationsUI.NotificationsSettings>
            {tablesData.map((ns) => (
              <NotificationsSettingsTable
                header={ns.header}
                data={ns.events}
                key={ns.header}
              />
            ))}
          </NotificationsUI.NotificationsSettings>
          <NotificationsUI.ActionRow>
            <PrimaryButton
              type="submit"
              loading={updating}
              disabled={isSubmitting || !isDirty}
            >
              Save
            </PrimaryButton>
            <SecondaryButton
              onClick={() => formMethods.reset()}
              isOutlined
              disabled={isSubmitting || !isDirty}
            >
              Reset
            </SecondaryButton>
          </NotificationsUI.ActionRow>
        </form>
      </FormProvider>
    </NotificationsUI.Container>
  );
};

export default Notifications;
